import React, { useState, useEffect } from 'react';
import { Translator } from './translator';
import { useLanguage } from "./LanguageContext";

const baseTextByLang = {
  it: {
    header: "Inizia su Te.C.H-e",
    email: "Email",
    password: "Password",
    continue: "Continua",
    resetpassword: "Hai dimenticato la password?",
    legalLine1: "Continuando, accetti i termini di Te.C.H-e",
    legalLine2: "Condizioni d'uso",
    legalLine3: "e Informativa sulla privacy",
    new: "Nuovo su Te.C.H-e?",
    create: "Crea il tuo account Te.C.H-e",
    text1: "Bentornato, ",
    text2: "Le tue credenziali non sono corrette.",
    text3: "Qualcosa è andato storto. Riprova più tardi.",
    emailErrorRequired: "L'email è obbligatoria",
    emailErrorInvalid: "L'email non è valida",
    passwordErrorRequired: "La password è obbligatoria",
    passwordErrorTooShort: "La password deve contenere almeno 6 caratteri",
  },

  en: {
    header: "Start with Te.C.H-e",
    email: "Email",
    password: "Password",
    continue: "Continue",
    resetpassword: "Forgot your password?",
    legalLine1: "By continuing, you agree to Te.C.H-e's",
    legalLine2: "Terms of Use",
    legalLine3: "and Privacy Policy",
    new: "New to Te.C.H-e?",
    create: "Create your Te.C.H-e account",
    text1: "Welcome back, ",
    text2: "Your credentials are wrong.",
    text3: "Something went wrong. Please try again later.",
    emailErrorRequired: "Email is required",
    emailErrorInvalid: "Email is invalid",
    passwordErrorRequired: "Password is required",
    passwordErrorTooShort: "Password must have at least 6 characters",
  },

  fr: {
    header: "Commencez avec Te.C.H-e",
    email: "E-mail",
    password: "Mot de passe",
    continue: "Continuer",
    resetpassword: "Mot de passe oublié ?",
    legalLine1: "En continuant, vous acceptez les conditions de Te.C.H-e",
    legalLine2: "Conditions d’utilisation",
    legalLine3: "et Politique de confidentialité",
    new: "Nouveau sur Te.C.H-e ?",
    create: "Créez votre compte Te.C.H-e",
    text1: "Bon retour, ",
    text2: "Vos identifiants sont incorrects.",
    text3: "Une erreur s’est produite. Veuillez réessayer plus tard.",
    emailErrorRequired: "L’e-mail est obligatoire",
    emailErrorInvalid: "L’e-mail n’est pas valide",
    passwordErrorRequired: "Le mot de passe est obligatoire",
    passwordErrorTooShort: "Le mot de passe doit contenir au moins 6 caractères",
  },
};

const Login = ({ setUser }) => {
  const safe = (val) => (!val || val === "undefined" ? "" : val);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({ email: '', password: '' });

  const { translateText, lang } = Translator();
  const { setLang } = useLanguage();

  const [translatedText, setTranslatedText] = useState(
    baseTextByLang[lang] || baseTextByLang.en
  );

  
  useEffect(() => {
    if (lang === "it" || lang === "en" || lang === "fr") {
      setTranslatedText(baseTextByLang[lang] || baseTextByLang.en);
      return;
    }

    // Other languages → auto-translate from Italian base
    let cancelled = false;
    const translateAll = async () => {
      try {
        const source = baseTextByLang.it;
        const entries = Object.entries(source);
        const translatedEntries = await Promise.all(
          entries.map(async ([key, value]) => {
            const out = await translateText(value);
            return [key, out || value];
          })
        );
        if (!cancelled) {
          setTranslatedText(Object.fromEntries(translatedEntries));
        }
      } catch {
        if (!cancelled) {
          setTranslatedText(baseTextByLang.en);
        }
      }
    };

    translateAll();
    return () => {
      cancelled = true;
    };
  }, [lang, translateText]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: '' });
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = translatedText.emailErrorRequired;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = translatedText.emailErrorInvalid;
    }
    if (!formData.password) {
      newErrors.password = translatedText.passwordErrorRequired;
    } else if (formData.password.length < 6) {
      newErrors.password = translatedText.passwordErrorTooShort;
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    const { email, password } = formData;
    try {
      const [loginRes, userIdRes] = await Promise.all([
        fetch(`https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com/loginUser?email=${email}&password=${password}`),
        fetch(`https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com/getUserID?email=${email}`)
      ]);
      const loginData = await loginRes.json();
      const userIdData = await userIdRes.json();

      if (Array.isArray(loginData) && loginData.length > 0) {
        const user = loginData[0];
        const userProps = user.props || {};
        alert(`${translatedText.text1}${userProps.firstName || userProps.uName || 'User'}`);
        const finalUser = {
          email: userProps.email || email,
          userID: user.userID,
          firstName: safe(userProps.firstName),
          lastName: safe(userProps.lastName),
          uName: safe(userProps.uName),
          reg_date: safe(userProps.reg_date),
          password: safe(userProps.Password),
          profilePic: safe(userProps.image),
          preferredLanguage: safe(userProps.preferredLanguage) || "en",
          gender: safe(userProps.gender),
          birthDate: safe(userProps.birthDate),
          telefono: safe(userProps.telefono),
          indirizzo: safe(userProps.indirizzo),
          nazione: safe(userProps.nazione),
          cap: safe(userProps.cap),
          labels: user.labels || []
        };
        setUser(finalUser);
        setLang(finalUser.preferredLanguage || "en");
        localStorage.setItem("user", JSON.stringify(finalUser));

        window.dispatchEvent(new CustomEvent("auth:login", { detail: finalUser }));
      } else {
        alert(translatedText.text2);
      }
    } catch (error) {
      alert(translatedText.text3);
    }
  };

  return (
    <div className="flex flex-col items-center min-h-screen bg-gray-100 p-6">
      <img src="/LOGO TECHE letters.png" alt="Logo" className="w-40 h-auto mb-4" />
      <div className="w-full max-w-md bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-transform">
        <h2 className="text-xl font-bold text-center mb-6 text-black">
          {translatedText.header}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-black mb-1">
              {translatedText.email}:
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={translatedText.email}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
            <p className="text-red-600 text-sm font-semibold mt-1">
              {errors.email}
            </p>
          </div>
          <div>
            <label className="block text-sm font-semibold text-black mb-1">
              {translatedText.password}:
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder={translatedText.password}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
            <p className="text-red-600 text-sm font-semibold mt-1">
              {errors.password}
            </p>
          </div>
          <button
            type="submit"
            className="w-full bg-[#324B8E] text-white py-2 rounded-md hover:bg-[#223b78] transition-all duration-300"
          >
            {translatedText.continue}
          </button>
        </form>
        <div className="text-center mt-3">
          <a
            href="/resetpassword"
            className="text-sm text-blue-600 no-underline hover:no-underline"
          >
            {translatedText.resetpassword}
          </a>
        </div>
        <div className="text-sm text-gray-700 mt-6">
          <p className="mb-2">
            {translatedText.legalLine1}{' '}
            <a
              href="/conditions"
              className="text-blue-600 no-underline hover:no-underline"
            >
              {translatedText.legalLine2}
            </a>{' '}
            <a
              href="/privacy"
              className="text-blue-600 no-underline hover:no-underline"
            >
              {translatedText.legalLine3}
            </a>.
          </p>
          <hr className="my-3 border-gray-300" />
          <p>
            {translatedText.new}{' '}
            <a
              href="/register"
              className="text-blue-600 no-underline hover:no-underline"
            >
              {translatedText.create}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
